import { useCallback, useEffect, useState } from "react";
import { MinerSettingsPoolProps } from "./MinerSettingsPool";
import { useAuth } from "@utils/hooks/useAuth";
import { ALGOS_E } from "@constants/Algos";
import { IPoolCreds } from "@services/getSettings";
import { IMinerInfoSetting } from "@services/getMinerInfo";
import { useMinerContext } from "../../../MinerContext";
import useSettingsChange from "@components/Pages/Machines/useSettingsChange";

export interface ISelectOption {
  readonly key: number;
  readonly label: string;
  readonly value: number;
  readonly disabled: boolean;
  readonly algo?: ALGOS_E;
  readonly login?: string;
  readonly name?: string;
  readonly pools?: IPoolCreds[];
}

function mapInfoToOption(item: IMinerInfoSetting): ISelectOption {
  return {
    key: item.id,
    value: item.id,
    name: item.name,
    label: item.email,
    algo: item.hash,
    login: item.Pools[0]?.login ?? "",
    pools: item.Pools,
    disabled: false,
  };
}

export default function useMinerSettingsPoolControl({
  settingId,
  settings,
  isAdminMiningOn,
}: MinerSettingsPoolProps) {

  const auth = useAuth();
  const { miner } = useMinerContext();
  const { onChange: doSettingsChange } = useSettingsChange(miner.id.toString());
  const [options, setOptions] = useState<ISelectOption[]>([]);
  const [value, setValue] = useState<ISelectOption>();

  const onChange = useCallback((id: number) => {
    if (isAdminMiningOn && !auth.isAdmin) return;
    const item = options.find(o => o.value === id);
    if (item) setValue(item);
    doSettingsChange({
      settingId: id
    })
  }, [options, setValue]);

  useEffect(() => {
    const groupped = settings.reduce<Record<string, ISelectOption[]>>((acc, item) => {
      const option = mapInfoToOption(item);
      if (!acc[item.email]) {
        acc[item.email] = [option];
      } else {
        acc[item.email].push(option);
      }
      return acc;
    }, {});

    const result: ISelectOption[] = [];

    Object.entries(groupped)
      .sort(([emailA], [emailB]) => {
        if (emailA === auth.email.value) return -1; // emailA в начало
        if (emailB === auth.email.value) return 1;  // emailB в начало
        return emailA.localeCompare(emailB);
      })
      .forEach(([email, opts], i) => {
        result.push({
          key: i,
          label: email,
          value: -1,
          disabled: true
        });
        result.push(...opts);
      });
    setOptions(result.slice(1));

  }, [settingId, settings, setOptions]);

  useEffect(() => {
    if (settingId !== undefined) {
      const initialValue = options.find(r => r.value === settingId);
      if (initialValue && initialValue.value > 0) {
        setValue(initialValue);
      }
    } else {
      setValue(undefined);
    }
  }, [settingId, options, setValue]);

  const getOptionById = useCallback((id: number) => {
    return options.find(o => o.value === id);
  }, [options]);

  return {
    options,
    value,
    isBlocked: isAdminMiningOn && !auth.isAdmin,
    onChange,
    getOptionById,
  }
}